import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, ShoppingBag } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ProductRow } from './ProductRow';
import { ProductCard } from './ProductCard';
import { sourceMeta } from './sourceMeta';
import { containerVariants } from './motion';
import type { Product, ProductSource } from '../mockKnowledge';

interface ProductsTableProps {
  products: Product[];
  onToggleInKnowledgeBase: (id: string) => void;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
}

const SOURCE_PILLS: { id: ProductSource | 'all'; label: string }[] = [
  { id: 'all', label: 'All Sources' },
  { id: 'shopify', label: 'Shopify' },
  { id: 'woocommerce', label: 'WooCommerce' },
  { id: 'manual', label: 'Manual' },
  { id: 'csv', label: 'CSV / Excel' },
  { id: 'sheet', label: 'Google Sheet' },
];

export function ProductsTable({ products, onToggleInKnowledgeBase, onEdit, onDelete }: ProductsTableProps) {
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<ProductSource | 'all'>('all');

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return products.filter((product) => {
      const matchesSource = sourceFilter === 'all' || product.source === sourceFilter;
      const matchesQuery = query.length === 0 || product.title.toLowerCase().includes(query);
      return matchesSource && matchesQuery;
    });
  }, [products, search, sourceFilter]);

  const listKey = `${sourceFilter}:${search.trim().toLowerCase()}`;

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800/90 bg-slate-50/40 dark:bg-slate-900/60 shadow-xs">
      {/* Pod Header */}
      <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70 bg-slate-100/50 dark:bg-slate-900/80 px-4 py-2.5 gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
            <ShoppingBag className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Product Catalog
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/80 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-400">
            {filtered.length} Indexed
          </span>
        </div>

        {/* Search input */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search products…"
            aria-label="Search products"
            className="h-8 w-56 rounded-full border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 pl-9 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary/40 focus:ring-primary/20 shadow-2xs"
          />
        </div>
      </div>

      {/* Source Quick Filter Pills Strip */}
      <div className="border-b border-slate-200/70 dark:border-slate-800/70 bg-white dark:bg-slate-900 px-4 py-2 flex items-center gap-1.5 overflow-x-auto scrollbar-none">
        {SOURCE_PILLS.map((pill) => {
          const isActive = sourceFilter === pill.id;
          return (
            <button
              key={pill.id}
              type="button"
              onClick={() => setSourceFilter(pill.id)}
              className={cn(
                'inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-all duration-150',
                isActive
                  ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-xs'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white shadow-2xs'
              )}
            >
              <span>{pill.label}</span>
            </button>
          );
        })}
      </div>

      {/* Desktop Table */}
      <div className="hidden overflow-x-auto md:block bg-white dark:bg-slate-900">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              <th className="py-2.5 pl-4 pr-3">Product</th>
              <th className="py-2.5 pr-3">Price</th>
              <th className="py-2.5 pr-3">Stock</th>
              <th className="py-2.5 pr-3">Source</th>
              <th className="py-2.5 pr-3">In AI Knowledge</th>
              <th className="py-2.5 pr-4 text-right">Actions</th>
            </tr>
          </thead>
          <motion.tbody key={listKey} variants={containerVariants} initial="hidden" animate="visible">
            <AnimatePresence>
              {filtered.map((product) => (
                <ProductRow
                  key={product.id}
                  product={product}
                  onToggleInKnowledgeBase={onToggleInKnowledgeBase}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </AnimatePresence>
          </motion.tbody>
        </table>
        {filtered.length === 0 && (
          <p className="py-8 text-center text-xs text-slate-400 dark:text-slate-500">No products match your search or filter.</p>
        )}
      </div>

      {/* Mobile Stacked Cards */}
      <motion.div key={listKey} variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 gap-2 p-3 md:hidden bg-white dark:bg-slate-900">
        <AnimatePresence>
          {filtered.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onToggleInKnowledgeBase={onToggleInKnowledgeBase}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </AnimatePresence>
      </motion.div>
      {filtered.length === 0 && (
        <p className="py-8 text-center text-xs text-slate-400 dark:text-slate-500 md:hidden bg-white dark:bg-slate-900">No products match your search or filter.</p>
      )}
    </Card>
  );
}

export default ProductsTable;
